import { Alert, Container, Snackbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BottomNav from '@/components/BottomNav';
import { LoadingSpinner } from '@/components/Loading';
import SubmissionForm from '@/components/SubmissionForm';
import { defaultSubmission } from '@/consts';
import { useAuth } from '@/contexts/AuthContext';
import { useEvent } from '@/contexts/EventsContext';
import { eventRunSubmission, generateRaceCode } from '@/utils/api';

/** @typedef { import('@mui/material').AlertProps } AlertProps */

export default function RunSubmission() {
  const [formData, setFormData] = useState({ ...defaultSubmission, incentives: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alert, setAlert] = useState({
    message: '',
    severity: /** @type { AlertProps['severity'] } */ ('info'),
  });
  const { user } = useAuth();
  const { open: events } = useEvent();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const resetFields = () => setFormData({ ...defaultSubmission, incentives: [] });

  useEffect(() => {
    if (alert.severity !== 'info') setAlertOpen(true);
  }, [alert]);

  useEffect(() => {
    (async () => {
      const raceCode = formData.isRace || formData.isCoop ? await generateRaceCode() : '';
      setFormData(prevFormData => ({ ...prevFormData, raceCode }));
    })();
  }, [formData.isRace || formData.isCoop]);

  const handleSubmit = async () => {
    const response = await eventRunSubmission(formData);
    // @ts-expect-error: TODO: Handle after we cleaned up our client api
    setAlert(response);
    if (['warning', 'error'].includes(response.severity)) return;
    resetFields();
  };

  if (isLoading) {
    return <LoadingSpinner setIsLoading={setIsLoading} />;
  }

  if (events.length === 0) {
    return (
      <Container maxWidth="xl">
        <Typography gutterBottom variant="h2" className="font-bebas">
          {t('events.submissionsNotOpen')}
        </Typography>
        {isMobile && <BottomNav />}
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="xl">
        <Typography gutterBottom variant="h2" className="font-bebas">
          {t('common.pleaselogin')}
        </Typography>
        {isMobile && <BottomNav />}
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <SubmissionForm
        formData={formData}
        events={events}
        setData={setFormData}
        submit={handleSubmit}
      />
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setAlertOpen(false)} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      {isMobile && <BottomNav />}
    </Container>
  );
}
